import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { Menu, X, ChevronDown } from "lucide-react";
import { useI18n, localeNames, locales, routes } from "@/i18n";
import { equivalentPath } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import PrefetchLink from "@/components/PrefetchLink";
import { PrefetchKey } from "@/lib/routePrefetchers";
import logoLight from "@/assets/logo-light-bg.png";
import logoDark from "@/assets/logo-dark-bg.webp";

const Header = () => {
  const { locale, t } = useI18n();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const r = routes[locale];

  // Pages that have dark/image heroes and need transparent header
  const isHomePage = location.pathname === `/${locale}` || location.pathname === `/${locale}/`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close lang dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navLinks: { label: string; path: string; prefetch: PrefetchKey }[] = [
    { label: t.nav.services, path: r.services, prefetch: "services" },
    { label: t.nav.industries, path: r.industries, prefetch: "industries" },
    { label: t.nav.approach, path: r.approach, prefetch: "approach" },
    { label: t.nav.about, path: r.about, prefetch: "about" },
    { label: t.nav.insights, path: r.insights, prefetch: "insights" },
  ];

  // When on hero pages and not scrolled: transparent with white text
  const isTransparent = !scrolled && !mobileOpen;

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-all duration-200 ${
        isTransparent
          ? "bg-transparent border-b border-transparent"
          : "bg-background/95 backdrop-blur-md border-b border-border/40 shadow-xs"
      }`}
    >
      <div className="container flex h-20 items-center justify-between md:h-24">
        <Link to={`/${locale}`} className="relative flex items-center h-10 md:h-12 w-[129px]">
          <img
            src={logoDark}
            alt="Stahl Swanson Consulting"
            width={129}
            height={48}
            decoding="async"
            fetchPriority="high"
            className={`absolute inset-0 h-full w-auto transition-opacity duration-200 ${isTransparent ? "opacity-100" : "opacity-0"}`}
          />
          <img
            src={logoLight}
            alt=""
            aria-hidden="true"
            width={129}
            height={48}
            decoding="async"
            fetchPriority="high"
            className={`absolute inset-0 h-full w-auto transition-opacity duration-200 ${isTransparent ? "opacity-0" : "opacity-100"}`}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-8 lg:flex">
          {navLinks.map((link) => (
            <PrefetchLink
              key={link.path}
              to={`/${locale}${link.path}`}
              prefetch={link.prefetch}
              className={`text-[13px] font-medium tracking-wide uppercase transition-colors ${
                isTransparent
                  ? "text-white/80 hover:text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
            </PrefetchLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-colors ${
                isTransparent
                  ? "text-white/70 hover:text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {localeNames[locale]}
              <ChevronDown className={`h-3 w-3 transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
              <div hidden={!langOpen} className="absolute top-full right-0 mt-1 bg-background border border-border rounded shadow-lg py-1 min-w-[80px] z-50">
                {locales.filter(l => l !== locale).map((l) => (
                  <Link
                    key={l}
                    to={equivalentPath(location.pathname, l)}
                    hrefLang={l}
                    onClick={() => { setMobileOpen(false); setLangOpen(false); }}
                    className="block w-full text-left px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    {localeNames[l]}
                  </Link>
                ))}
              </div>
          </div>
          <Button
            asChild
            size="sm"
            className={`${
              isTransparent
                ? "bg-white/10 backdrop-blur-xs border border-white/30 text-white hover:bg-white/20"
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            } transition-all duration-300`}
          >
            <PrefetchLink to={`/${locale}${r.contact}`} prefetch="contact">{t.nav.contact}</PrefetchLink>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <X className={`h-5 w-5 ${isTransparent && !mobileOpen ? "text-white" : ""}`} />
          ) : (
            <Menu className={`h-5 w-5 ${isTransparent ? "text-white" : ""}`} />
          )}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="border-t border-border bg-background lg:hidden">
          <nav className="container flex flex-col gap-4 py-6">
            {navLinks.map((link) => (
              <PrefetchLink
                key={link.path}
                to={`/${locale}${link.path}`}
                prefetch={link.prefetch}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </PrefetchLink>
            ))}
            <div className="flex items-center gap-2 pt-2">
              {locales.map((l) => (
                <Link
                  key={l}
                  to={equivalentPath(location.pathname, l)}
                  hrefLang={l}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded border ${
                    l === locale
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {localeNames[l]}
                </Link>
              ))}
            </div>
            <Button asChild className="mt-2">
              <PrefetchLink to={`/${locale}${r.contact}`} prefetch="contact" onClick={() => setMobileOpen(false)}>
                {t.nav.contact}
              </PrefetchLink>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
